import history from '../../../shared/history';
import { constants } from '../../../shared/enums/constants';
import { StorageUtil } from '../../../shared/utils/storage';
import { COMPANY_FUNDING_ROUND_DATA, INDUSTRY_DATA } from './filter-data';
import { V2Lead, LeadFinderSecondaryTab } from '../types';

export const validateNumberForQueryParams = (
  value: string | null,
  fallbackValue = 1,
) =>
  value === null || Number.isNaN(Number(value)) || Number(value) < 1
    ? fallbackValue
    : Number(value);

export const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const getExceptFilterValues = (keys: string[], list: any[]) => {
  if (!keys?.length) {
    return [];
  }
  return keys.filter(
    (value) =>
      !list.some(
        (item) =>
          item.key.toLowerCase() === value.toLowerCase() ||
          (item.children &&
            item.children.some(
              (childItem) =>
                childItem.key.toLowerCase() === value.toLowerCase(),
            )),
      ),
  );
};

export const getExceptFilterKeys = (values: string[], list: any[]) => {
  if (!values?.length) {
    return [];
  }
  return values.filter(
    (value) =>
      !list.some(
        (item) =>
          item.value.toLowerCase() === value.toLowerCase() ||
          (item.children &&
            item.children.some(
              (childItem) =>
                childItem.value.toLowerCase() === value.toLowerCase(),
            )),
      ),
  );
};

export const getFilterValues = (keys: string[], list: any[]) => {
  const values: string[] = [];
  if (!keys?.length || !list?.length) return values;

  keys.forEach((key) => {
    list.some((item) => {
      if (item?.key?.toLowerCase() === key?.toLowerCase()) {
        values.push(item?.value || item?.label);
        return true;
      }
      if (item?.children?.length) {
        const child = item.children.find(
          (childItem) => childItem.key.toLowerCase() === key.toLowerCase(),
        );
        if (child) {
          values.push(child.value || child?.label);
          return true;
        }
      }
      return false;
    });
  });

  return values;
};

/**
 * Formats a numeric range string for display with comma-separated thousands.
 * e.g. "1000-5000" -> "1,000-5,000", "10000+" -> "10,000+"
 * Returns original value for non-numeric formats (select, custom, etc.).
 */
export const formatNumericRangeForDisplay = (value: string): string => {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (
    trimmed === '' ||
    trimmed === 'select' ||
    trimmed.toLowerCase() === 'custom'
  ) {
    return value;
  }
  if (trimmed.endsWith('+')) {
    const num = parseInt(trimmed.slice(0, -1), 10);
    if (!Number.isNaN(num)) {
      return `${num.toLocaleString('en-US')}+`;
    }
    return value;
  }
  if (trimmed.startsWith('<')) {
    const num = parseInt(trimmed.slice(1), 10);
    if (!Number.isNaN(num)) {
      return `<${num.toLocaleString('en-US')}`;
    }
    return value;
  }
  if (trimmed.includes('-')) {
    const [minStr, maxStr] = trimmed.split('-').map((s) => s.trim());
    const minNum = parseInt(minStr, 10);
    const maxNum = parseInt(maxStr, 10);
    if (!Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
      return `${minNum.toLocaleString('en-US')}-${maxNum.toLocaleString(
        'en-US',
      )}`;
    }
  }
  return value;
};

/**
 * Formats min/max range for display with comma-separated thousands.
 * Used for object-based ranges like { min, max }.
 */
export const formatMinMaxRange = (
  min: number | string | undefined,
  max?: number | string | undefined,
): string => {
  const minNum =
    typeof min === 'number' ? min : parseInt(String(min ?? ''), 10);
  const maxNum =
    typeof max === 'number' ? max : parseInt(String(max ?? ''), 10);
  const minValid = !Number.isNaN(minNum);
  const maxValid = !Number.isNaN(maxNum);
  if (minValid && maxValid) {
    return `${minNum.toLocaleString('en-US')}-${maxNum.toLocaleString(
      'en-US',
    )}`;
  }
  if (minValid && !maxValid) {
    return `${minNum.toLocaleString('en-US')}+`;
  }
  if (!minValid && maxValid) {
    return `<${maxNum.toLocaleString('en-US')}`;
  }
  return '';
};

export const getFilterValuesWithExtraValues = (
  keys: string[],
  list: any[],
  locationData?: boolean,
) => {
  const values = getFilterValues(keys, list);
  const extraValues = getExceptFilterValues(keys, list);
  const parentKeys = keys?.filter((key) => key?.length === 2);

  const queryParam = new URLSearchParams(history?.location?.search);
  const locationFilterKey =
    queryParam.get('location') || queryParam.get('excludeLocation');

  const exactMatch = (locationFilterKey?.match(/"/g) || [])?.length === 2;

  let data = [...values, ...extraValues];

  if (exactMatch && locationData) {
    const result: string[] = [];
    keys?.forEach((key) => {
      if (parentKeys?.length) {
        parentKeys.forEach((parentKey) => {
          if (!key?.substring(0, 2).includes(parentKey)) {
            if (!result.includes(key)) {
              result.push(key);
            }
          }
        });
      } else if (!result.includes(key)) {
        result.push(key);
      }
    });
    const resultValues = getFilterValues(result, list);

    data = data.map((value) =>
      resultValues.includes(value) ? `"${value}"` : value,
    );
  }

  return data;
};

export const getFilterKeys = (values: string[], list: any[]) => {
  const keys: string[] = [];

  values.forEach((value) => {
    list.some((item) => {
      if (item.value.toLowerCase() === value.toLowerCase()) {
        keys.push(item.key);
        return true;
      }
      if (item?.children?.length) {
        const child = item.children.find(
          (childItem) => childItem.value.toLowerCase() === value.toLowerCase(),
        );
        if (child) {
          keys.push(child.key);
          return true;
        }
      }
      return false;
    });
  });

  return keys;
};

export const getFilterKeysWithExtraKeys = (keys: string[], list: any[]) => [
  ...getFilterKeys(keys, list),
  ...getExceptFilterKeys(keys, list),
];

export const findParentKey = (data: any[], targetKey: string) => {
  let parentKey: string | null = null;
  data.some((item) => {
    if (item?.children?.length) {
      const foundChild = item.children.find(
        (child) => child?.key?.toLowerCase() === targetKey?.toLowerCase(),
      );
      if (foundChild) {
        parentKey = item.key;
        return true;
      }
    }
    return false;
  });
  return parentKey;
};

/**
 * Expands parent industry keys to their children values.
 * When a parent key is provided, it returns all children values instead of the parent.
 * For child keys or non-matching keys, it returns the original value.
 */
export const expandIndustryParentToChildren = (
  keys: string[],
  industryData: any[],
): string[] => {
  const expandedValues: string[] = [];

  keys.forEach((key) => {
    // Find if this key is a parent key
    const parentItem = industryData.find(
      (item) => item?.key?.toLowerCase() === key?.toLowerCase(),
    );

    if (parentItem && parentItem.children?.length) {
      // It's a parent key - add all children values instead
      parentItem.children.forEach((child) => {
        const childValue = child.value || child.key;
        if (!expandedValues.includes(childValue)) {
          expandedValues.push(childValue);
        }
      });
    } else {
      // Check if it's a child key or a custom value
      let foundChildValue: string | null = null;

      // Find the child value by searching through all parent items
      industryData.some((item) => {
        if (item?.children?.length) {
          const child = item.children.find(
            (c) => c?.key?.toLowerCase() === key?.toLowerCase(),
          );
          if (child) {
            foundChildValue = child.value || child.key;
            return true; // Stop iterating
          }
        }
        return false;
      });

      if (foundChildValue) {
        // It's a child key - add the child value
        if (!expandedValues.includes(foundChildValue)) {
          expandedValues.push(foundChildValue);
        }
      } else if (!expandedValues.includes(key)) {
        // It's a custom/extra value not in the list - add as-is
        expandedValues.push(key);
      }
    }
  });

  return expandedValues;
};

export const getV2SavedTabPageSize = () => {
  try {
    const savedPageSize = StorageUtil.getItem('v2LeadFinderSavedTabPageSize');
    return savedPageSize
      ? Number(savedPageSize)
      : constants.LEAD_FINDER_DEFAULT_PAGE_SIZE;
  } catch (error) {
    return constants.LEAD_FINDER_DEFAULT_PAGE_SIZE;
  }
};

export const getTimeAgoString = (timestamp) => {
  const timestampDate: any = new Date(timestamp);
  const currentDate: any = new Date();
  const timeDifference = currentDate - timestampDate;
  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) {
    return 'Just Now';
  }
  if (minutes < 60) {
    return `${minutes} minutes ago`;
  }
  if (hours < 24) {
    return `${hours} hours ago`;
  }
  return `${days} days ago`;
};

export const capitalizeFirstLetter = (string = '') => {
  if (string) {
    return string?.charAt(0).toUpperCase() + string?.slice(1);
  }
  return '';
};

export const mapObjectToString = (
  obj,
  companyListsData,
  peopleListsData,
  companyIdNameMap?: Record<string, string>,
) => {
  let mappedString = '';

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in obj) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
      let formattedValue;
      const value = obj[key];

      if (Array.isArray(value)) {
        // Handle arrays
        if (key === 'visits_breakdown_by_gender') {
          const pct = value[0].percentage;
          const rangeStr = formatMinMaxRange(pct?.min, pct?.max);
          const isPercentage =
            pct?.min != null &&
            pct?.max != null &&
            Number(pct.min) <= 100 &&
            Number(pct.max) <= 100;
          formattedValue = rangeStr
            ? `${value[0].gender}: ${rangeStr}${isPercentage ? '%' : ''}`
            : `${value[0].gender}: ${value[0].percentage?.min}-${value[0].percentage?.max}%`;
        } else if (key === 'visits_breakdown_by_country') {
          formattedValue =
            value?.length > 1
              ? `${value[0].country}: ${value[0].percentage?.min ?? 0}-${
                  value[0].percentage?.max ?? 0
                }%+${value.length - 1}`
              : `${value[0].country}: ${value[0].percentage?.min ?? 0}-${
                  value[0].percentage?.max ?? 0
                }%`;
        } else if (
          key === 'employee_count_seniority' ||
          key === 'employee_count_department'
        ) {
          formattedValue =
            value?.length > 1
              ? `${value[0].group}: ${value[0].count?.min ?? 0}-${
                  value[0].count?.max ?? 0
                }+${value.length - 1}`
              : `${value[0].group}: ${value[0].count?.min ?? 0}-${
                  value[0].count?.max ?? 0
                }`;
        } else if (key === 'base_salary') {
          formattedValue =
            value?.length > 1
              ? `${value[0]?.title?.includes?.[0] ?? ''}: ${
                  value[0].range?.min ?? 0
                }-${value[0].range?.max ?? 0}+${value.length - 1}`
              : `${value[0]?.title?.includes?.[0] ?? ''}: ${
                  value[0].range?.min ?? 0
                }-${value[0].range?.max ?? 0}`;
        } else if (key === 'look_alike_company_name') {
          formattedValue =
            value?.length > 1
              ? `${value[0]?.value ?? ''}+${value.length - 1}`
              : `${value[0]?.value ?? ''}`;
        } else {
          formattedValue =
            value?.length > 1 ? `${value[0]}+${value.length - 1}` : value[0];
        }
      } else if (typeof value === 'object' && value !== null) {
        // Handle nested objects (new filter format)
        const parts = [];

        if (value.includes && value.includes.length > 0) {
          let firstValue = value.includes[0];
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          if (key === 'companyList') {
            const companyListName = companyListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (companyListName) {
              firstValue = companyListName;
            }
          }
          if (key === 'peopleList') {
            const peopleListName = peopleListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (peopleListName) {
              firstValue = peopleListName;
            }
          }
          if (key === 'company_ids' && companyIdNameMap) {
            const resolved = companyIdNameMap[String(firstValue)];
            if (resolved != null) {
              firstValue = resolved;
            }
          }

          const includeStr =
            value.includes.length > 1
              ? `${firstValue}+${value.includes.length - 1}`
              : firstValue;

          parts.push(includeStr);
        }
        if (value.title?.includes && value.title?.includes.length > 0) {
          let firstValue = value.title?.includes[0];
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          const includeStr =
            value.title?.includes.length > 1
              ? `${firstValue}+${value.includes.length - 1}`
              : firstValue;

          parts.push(includeStr);
        }
        if (value.title?.excludes && value.title?.excludes.length > 0) {
          const excludeStr =
            value.title?.excludes.length > 1
              ? `exclude ${value.title?.excludes[0]}+${
                  value.title?.excludes.length - 1
                }`
              : `exclude ${value.title?.excludes[0]}`;
          parts.push(excludeStr);
        }

        if (value.excludes && value.excludes.length > 0) {
          let firstValue = value.excludes[0];
          if (value.exactMatch === 1) {
            firstValue = `"${firstValue}"`;
          }
          if (key === 'companyList') {
            const companyListName = companyListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (companyListName) {
              firstValue = companyListName;
            }
          }
          if (key === 'peopleList') {
            const peopleListName = peopleListsData?.find(
              (list) => list.id === firstValue,
            )?.label;
            if (peopleListName) {
              firstValue = peopleListName;
            }
          }
          if (key === 'company_ids' && companyIdNameMap) {
            const resolved = companyIdNameMap[String(firstValue)];
            if (resolved != null) {
              firstValue = resolved;
            }
          }
          const excludeStr =
            value.excludes.length > 1
              ? `exclude ${firstValue}+${value.excludes.length - 1}`
              : `exclude ${firstValue}`;
          parts.push(excludeStr);
        }

        if (value.shouldSearchPast === 1 || value.shouldSearchPast === true) {
          parts.push('Include Past companies');
        }

        if (value.min && value.max) {
          parts.push(`${value.min}-${value.max}`);
        }
        if (value.min && !value.max) {
          parts.push(`${value.min}+`);
        }
        if (!value.min && value.max) {
          parts.push(`<${value.max}`);
        }
        if (value.isUnknown !== undefined) {
          if (value.isUnknown === 1) {
            parts.push('isUnknown');
          } else {
            parts.push('isKnown');
          }
        }
        if (value.exists === true) {
          parts.push('all time');
        }

        if (value.duration && value.direction && value.points_range) {
          parts.push(
            `${value.duration}: ${value.direction}: ${
              value.points_range?.min ?? 0
            }-${value.points_range?.max ?? 0}`,
          );
        }

        formattedValue = parts.join(', ') || value.toString();
      } else if (
        key === 'decision_maker' ||
        key === 'is_b2b' ||
        key === 'company_bootstraped'
      ) {
        if (value === 1) {
          formattedValue = 'Yes';
        } else {
          formattedValue = 'No';
        }
      } else {
        // Handle primitives
        formattedValue = value.toString();
      }

      if (formattedValue) {
        mappedString += `${formattedValue}, `;
      }
    }
  }

  // Remove the trailing comma and space
  mappedString = mappedString.slice(0, -2);

  return mappedString;
};

export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY =
  'v2LeadFinderRecentSearches';

export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_PEOPLE = V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY;
export const V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY =
  'v2LeadFinderRecentSearchesCompany';

export const LEAD_PROFILE_MODAL_SIZE = 'v2LeadProfileModalSize';
export const LEAD_COMPANY_PROFILE_MODAL_SIZE = 'v2LeadCompanyProfileModalSize';
export const COMPANY_PROFILE_MODAL_SIZE = 'v2CompanyProfileModalSize';

export const LEADS_PER_PAGE = 25;
export const MAX_PAGINATION_TOTAL_ROWS = 10000;

export const MAPPED_KEYS = {
  skills: 'skill',
  school: 'school',
  job_title: 'job-title',
  department: 'department',
  active_job_postings_title: 'job-title',
  base_salary_title: 'job-title',
};

export const V2_MAPPED_KEYS = {
  location: 'location',
  company_hq_location: 'location',
  visits_breakdown_by_country: 'location',
  technologies_used: 'technology',
};

export const LOCAL_SEARCH_MAPPED_KEYS = {
  company_industry: INDUSTRY_DATA,
  funding_rounds_name: COMPANY_FUNDING_ROUND_DATA,
};

export const NOT_ALLOWED_APPEND_CHECKBOXES = ['managementLevels'];

export const TEASER_FIELD_MAPPING: Record<string, boolean> = {
  full_name: true, // Name -> Yes
  job_title: true, // Job Title -> Yes
  company_name: true, // Company Name -> Yes
  emails: false, // Emails -> No
  phones: false, // Phones -> No
  department: true, // Department -> Yes
  social_link: false, // Social URLs -> No
  location_country: true, // Country -> Yes
  location_state: true, // State -> Yes
  location_city: true, // Location City -> Yes
  company_industry: false, // Company Industry -> No
  company_domain: false, // Company Domain -> No (not in interface, but include for completeness)
  company_annual_revenue: false, // Company Revenue -> No
  company_founded_year: false, // Company Founded Year -> No
  total_experience: true, // Total Experience -> Yes
  management_level: false, // Management Level -> No
  decision_maker: true, // Decision Maker -> Yes
  jobs_changed_within: false, // Jobs Changed Within -> No
  skills: false, // Skills -> No
  experience_at_current_company: false, // Current Company experience -> No
  current_role_tenure_months: false, // Tenure In Current Role -> No
  company_is_b2b: true, // Is B2b -> Yes
  company_logo_url: true, // Company Logo URL -> Yes
  company_socials: false, // Company socials -> No
  projected_base_salary_dollars: false, // Projected Base Salary in Dollars -> No
  company_hq_country: false, // Company HQ Location -> No
  company_hq_state: false, // Company HQ Location -> No
  company_hq_city: false, // Company HQ Location -> No
  company_last_funding_round_amount_raised: false, // Funding Amount Range -> No
  company_last_funding_date: false, // Latest Funding Date Range -> No
  institution_name: false, // School -> No
  education_degrees: false, // Education Degrees -> No
  linkedin_url: false, // LinkedIn URL -> (empty, treat as No)
  linkedin_connection_count: false, // LinkedIn Connection -> No
  linkedin_followers_count: false, // LinkedIn Followers -> No
  past_companies: false, // Past Companies -> No
  active_experiences: false, // Active Experiences -> No
  picture_url: true, // Picture URL -> Yes
  company_size_range: false, // Company Size Range -> No
  company_website: false, // Company Website -> No
  company_linkedin: false, // Company LinkedIn -> No
};

// Fields visible to free plan users even when lead is not revealed
export const FREE_PLAN_LEAD_ALLOWED_FIELDS: Set<string> = new Set([
  'full_name',
  'company_name',
  'company_domain',
  'company_logo_url',
  'picture_url',
  'company_website',
]);

// Helper to check if a lead has valid email (for Save as Prospect / Add to Sequence)
export const hasLeadValidEmail = (lead: V2Lead): boolean =>
  lead?.isRevealed === true && !!lead?.emails && lead.emails.length > 0;

// Helper function to check if a lead should be disabled
export const isLeadDisabled = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
): boolean => {
  const isRevealing =
    lead?.isRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true;

  return (
    (lead?.isRevealed === true &&
      lead?.reReveal === false &&
      (!lead?.emails || lead?.emails?.length === 0)) ||
    isRevealing
  );
};

export const isLeadDisabledForSavedTab = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
): boolean => {
  const isRevealing =
    lead?.isRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true;

  // In saved tab, only disable when revealing - allow selecting leads without
  // valid email so they can be added to list (Add to List doesn't require email)
  return isRevealing;
};

export const getRowDisabledTooltip = (
  lead: V2Lead,
  emailRevealingLeads: Record<number, boolean> = {},
  emailPhoneRevealingLeads: Record<number, boolean> = {},
  activeSubTab: LeadFinderSecondaryTab,
  isSelectedRowsLimitReached = false,
  maxSelectedRowsLimit = 0,
): string => {
  // Check if lead is revealing
  const isRevealing =
    lead?.isRevealing === true ||
    emailRevealingLeads[lead?.id] === true ||
    emailPhoneRevealingLeads[lead?.id] === true;

  if (isRevealing) {
    return 'Revealing Lead';
  }

  // Check if lead has no email (saved tab: allow selection for Add to List)
  if (
    activeSubTab !== LeadFinderSecondaryTab.SAVED &&
    lead?.isRevealed === true &&
    lead?.reReveal === false &&
    (!lead?.emails || lead?.emails?.length === 0)
  ) {
    return 'Lead does not have any email address';
  }

  // Check if selection limit is reached
  if (isSelectedRowsLimitReached && maxSelectedRowsLimit > 0) {
    return `Maximum ${maxSelectedRowsLimit} leads can be selected`;
  }

  return ''; // No tooltip needed
};

export const SAVED_TAB_BULK_OPERATION_LIMIT = 25000;

export const INSUFFICIENT_CREDITS_ERROR_MESSAGE = 'Insufficient credits';

export const isInsufficientCreditsError = (message: any) =>
  typeof message === 'string' &&
  message
    .toLowerCase()
    .includes(INSUFFICIENT_CREDITS_ERROR_MESSAGE.toLowerCase());

/**
 * Formats lead finder credits to show up to 2 decimal places, removing only .00
 * @param credits - The number of credits to format
 * @returns Formatted string with credits (e.g., "100" for 100.00, "100.5" for 100.50)
 *
 * @example
 * formatLeadFinderCredits(100); // "100"
 * formatLeadFinderCredits(100.5); // "100.5"
 * formatLeadFinderCredits(100.50); // "100.5"
 * formatLeadFinderCredits(100.00); // "100"
 */
export const formatLeadFinderCredits = (credits: number): string => {
  const truncated = Math.floor(credits * 100) / 100;
  const formatted = truncated.toFixed(2);
  return formatted.replace(/\.00$/, '');
};

// Helper to get min/max range structure
// Helper function to convert company_founded_year relative keys to actual year range
export const convertYearKeyToRange = (key: string): string | null => {
  if (!key) return null;

  const currentYear = new Date().getFullYear();
  const trimmedKey = key.trim(); // Remove any spaces like "1 -3"

  // FIRST: Check if it's already an absolute year range (e.g., "2017-2024")
  // This must be checked BEFORE relative range conversion to avoid misinterpreting custom ranges
  if (trimmedKey.includes('-')) {
    const parts = trimmedKey.split('-');
    if (parts.length === 2) {
      const part1 = parseInt(parts[0].trim(), 10);
      const part2 = parseInt(parts[1].trim(), 10);
      // Check if both parts are valid 4-digit years (absolute year range)
      if (
        !Number.isNaN(part1) &&
        !Number.isNaN(part2) &&
        part1 >= 1900 &&
        part1 <= currentYear &&
        part2 >= 1900 &&
        part2 <= currentYear
      ) {
        return trimmedKey; // Already an absolute year range, return as is
      }
    }
  }

  // SECOND: Handle relative year keys (only if not already an absolute range)
  if (trimmedKey.endsWith('+')) {
    // Handle "20+" format
    const yearsPast = parseInt(trimmedKey.replace('+', ''), 10);
    if (!Number.isNaN(yearsPast)) {
      const minYear = currentYear - yearsPast;
      return `${minYear}-${currentYear}`;
    }
  } else if (trimmedKey.includes('-')) {
    // Handle "1-3" or "1 -3" format (relative range)
    const parts = trimmedKey.split('-').map((p) => parseInt(p.trim(), 10));
    if (parts.length === 2 && !parts.some(Number.isNaN)) {
      // Check if it's a relative range (small numbers, not years)
      // If both parts are less than 100, treat as relative range
      if (parts[0] < 100 && parts[1] < 100) {
        // First number (1) represents current year, second (3) represents years in past
        const maxYear = currentYear; // First number = current year
        const minYear = currentYear - parts[1]; // Second number = years in past
        return `${minYear}-${maxYear}`;
      }
    }
  }

  return null; // Invalid format
};

export const isListFilter = (key: string): boolean => {
  if (key === 'company_list' || key === 'people_list') {
    return true;
  }
  return false;
};

/**
 * Append AI search filters to a URL object by serializing common filter shapes:
 * - arrays -> repeated params
 * - { includes: [...] } -> repeated params for key
 * - { excludes: [...] } -> set excludeKey as comma separated
 * - { min, max } -> set as "min-max"
 * - flags (isUnknown, exactMatch, shouldSearchPast) -> separate params
 *
 * Mutates the passed URL's searchParams.
 */
export const appendAiFiltersToUrl = (currentUrl: URL, filtersPayload: any) => {
  const aiSearchFilters = { ...filtersPayload };

  const appendExcludeKey = (key: string) =>
    `exclude${key.charAt(0).toUpperCase()}${key.slice(1)}`;

  const setFlagParam = (key: string, flagKey: string, value: any) => {
    if (value === 1 || value === '1' || value === true) {
      currentUrl.searchParams.set(`${key}_${flagKey}`, '1');
    } else if (value === 0 || value === '0' || value === false) {
      currentUrl.searchParams.set(`${key}_${flagKey}`, '0');
    }
  };

  if (Object.keys(aiSearchFilters)?.length) {
    const filterEntries = Object.entries(aiSearchFilters);
    filterEntries.forEach(([filterKey, filterValue]) => {
      // Remove existing keys for both include and exclude variants
      currentUrl.searchParams.delete(filterKey);
      currentUrl.searchParams.delete(appendExcludeKey(filterKey));
      // Also remove possible flag keys
      currentUrl.searchParams.delete(`${filterKey}_isUnknown`);
      currentUrl.searchParams.delete(`${filterKey}_exactMatch`);
      currentUrl.searchParams.delete(`${filterKey}_shouldSearchPast`);

      if (filterKey && filterValue !== undefined && filterValue !== null) {
        // Arrays -> append multiple values for the same key
        if (Array.isArray(filterValue)) {
          filterValue.forEach((value) => {
            if (value !== undefined && value !== null && value !== '') {
              currentUrl.searchParams.append(filterKey, String(value));
            }
          });
          return;
        }

        // Objects -> handle structured filters (includes/excludes/min/max/flags)
        if (typeof filterValue === 'object') {
          const obj: any = filterValue;

          // includes -> append repeated filterKey
          if (Array.isArray(obj.includes) && obj.includes.length > 0) {
            obj.includes.forEach((val: any) => {
              if (val !== undefined && val !== null && val !== '') {
                currentUrl.searchParams.append(filterKey, String(val));
              }
            });
          }

          // excludes -> append to excludeKey as comma-separated or repeated values
          if (Array.isArray(obj.excludes) && obj.excludes.length > 0) {
            currentUrl.searchParams.set(
              appendExcludeKey(filterKey),
              obj.excludes.map((v: any) => String(v)).join(','),
            );
          }

          // min/max -> set as "min - max"
          if (obj.min !== undefined || obj.max !== undefined) {
            const min =
              obj.min !== undefined && obj.min !== null ? String(obj.min) : '';
            const max =
              obj.max !== undefined && obj.max !== null ? String(obj.max) : '';
            currentUrl.searchParams.set(filterKey, `${min} - ${max}`);
          }

          // isUnknown flag
          if (obj.isUnknown !== undefined) {
            setFlagParam(filterKey, 'isUnknown', obj.isUnknown);
          }

          // exactMatch flag
          if (obj.exactMatch !== undefined) {
            setFlagParam(filterKey, 'exactMatch', obj.exactMatch);
          }

          // shouldSearchPast flag
          if (obj.shouldSearchPast !== undefined) {
            setFlagParam(filterKey, 'shouldSearchPast', obj.shouldSearchPast);
          }

          return;
        }

        // Primitive values -> set directly
        currentUrl.searchParams.set(filterKey, String(filterValue));
      }
    });
  }
};

export const RESTRICTED_PEOPLE_FILTER_IDS = new Set([
  'filter-jobs-changed-within',
  'filter-current-role-tenure',
  'filter-projected-base-salary',
  'filter-is-b2b',
  'filter-company-bootstraped',
  'filter-revenue',
  'filter-funding-amount',
  'filter-latest-funding-date',
]);

export const RESTRICTED_COMPANY_FILTER_IDS = new Set([
  'filter-funding',
  'filter-last-funding-round',
  'filter-employee-count',
  'filter-seniority-wise-employee-count',
  'filter-department-wise-employee-count',
  'filter-active-job-openings',
  'filter-last-month-website-visitors',
  'filter-visitor-by-gender',
  'filter-visitor-by-country',
  // 'filter-signals',
  // 'filter-news',
  'filter-salary-information',
  'filter-employee-reviews-business-outlook',
  'filter-employee-reviews-ceo-approval',
  'filter-employee-reviews-career-opportunities',
  'filter-employee-reviews-recommend',
]);

/**
 * Compute Lead Reveal credits using three-step precedence:
 * 1) If LEAD.REVEAL + LEAD.REVEAL.BUCKET2 > 0 => use that sum
 * 2) Else if leadFinder.isBucket1Frozen === 1 => use only BUCKET2
 * 3) Otherwise => use BUCKET1 + BUCKET2
 *
 * Inputs may be strings or numbers. Missing values treated as 0.
 */
export const computeLeadRevealCredits = (
  leadRevealMainRaw?: string | number,
  leadRevealBucket1Raw?: string | number,
  leadRevealBucket2Raw?: string | number,
  leadFinderMeta?: any,
): { availableCredits: number; totalCredits: number } => {
  const leadRevealMain = Number(leadRevealMainRaw) || 0;
  const bucket1 = Number(leadRevealBucket1Raw ?? 0);
  const bucket2 = Number(leadRevealBucket2Raw ?? 0);
  const isBucket1Frozen =
    String((leadFinderMeta as any)?.isBucket1Frozen) === '1';

  let availableCredits: number;
  if (leadRevealMain) {
    availableCredits = leadRevealMain + bucket2;
  } else if (isBucket1Frozen) {
    availableCredits = bucket2;
  } else {
    availableCredits = bucket1 + bucket2;
  }

  let totalCredits: number;
  if (leadRevealMain) {
    totalCredits = leadRevealMain + bucket2;
  } else {
    totalCredits = bucket1 + bucket2;
  }

  return { availableCredits, totalCredits };
};

// Delay the event
export const delay = () =>
  new Promise<void>((resolve) => setTimeout(() => resolve(), 0));
